import * as React from 'react';
import { FilterProviderBase, isGenericEmptyValue, QueryStringTarget } from '.';
import { Filter, FilterNotSet } from '..';
import {
	MultipleValueDelimiter,
	RangedValueDelimiter,
	MultipleDateRangePresets,
	getFriendlyDateRangePresetValueForDisplay,
	RangePresetValuePresets,
} from './Constants';
import { RangedPresetValue } from './Types';
import MultipleDateRangePresetFilterEdit from '../editors/MultipleDateRangePresetFilterEdit';
import moment from 'moment';

/**
 * Abstract class used to define filter type providers that support multiple values as well as ranges.
 * Inherit this class in order to implement your own. This adds a `getPresets` abstract method
 * that needs to be implemented, returning a dictionary with all the presets that we want to work with.
 * It also adds a `isValidRangePresetValue` method in order to help validate the values parsed from the
 * query string, to ensure they're valid.
 *
 * @export
 * @abstract
 * @class MultipleRangePresetProvider
 * @implements {FilterProviderBase<RangedPresetValue[]>}
 */
export abstract class MultipleRangePresetProvider implements FilterProviderBase<RangedPresetValue[]> {
	public getPresets = (): RangePresetValuePresets => ({});
	// eslint-disable-next-line no-unused-vars
	public isValidRangePresetValue = (value: RangedPresetValue): boolean => true;
	public getComponent = (): React.ComponentType => () => <>Component not set.</>;
	// eslint-disable-next-line no-unused-vars
	public getComponentProps = (filter: Filter) => ({});
	public getFriendlyValueForDisplay = (value: any[]): React.ReactNode => {
		if (value.length === 0) {
			return <FilterNotSet />;
		}

		return value.map(getFriendlyDateRangePresetValueForDisplay).join(', ');
	};

	public getValueFromQueryString = (query: string): RangedPresetValue[] | null => {
		// key=preset
		// key=fromvalue<range>tovalue
		// key=preset<value>foo<range>bar<value>baz<range>bar

		if (!query || !query.trim()) {
			return null;
		}

		const rangedPresetValues: RangedPresetValue[] = [];
		const values = query.split(MultipleValueDelimiter);
		const presets = this.getPresets() || {};

		for (const value of values) {
			const rangeOrPreset = value.split(RangedValueDelimiter);

			if (rangeOrPreset.length === 2) {
				const parsedValue: RangedPresetValue = {
					fromValue: rangeOrPreset[0],
					toValue: rangeOrPreset[1],
				};

				if (!this.isValidRangePresetValue(parsedValue)) {
					throw new Error('Invalid date range preset values found.');
				}

				rangedPresetValues.push(parsedValue);
			} else {
				const preset = presets[value];

				if (typeof preset === 'function') {
					const presetResult: RangedPresetValue = preset();
					rangedPresetValues.push(presetResult);
				} else {
					const parsedValue: RangedPresetValue = {
						fromValue: rangeOrPreset[0],
						toValue: null,
					};

					if (!this.isValidRangePresetValue(parsedValue)) {
						throw new Error('Invalid date range preset values found.');
					}

					rangedPresetValues.push(parsedValue);
				}
			}
		}

		return rangedPresetValues;
	};

	public getQueryStringFromValue = (
		value: RangedPresetValue[],
		filter: Filter,
		target: QueryStringTarget,
	): string => {
		if (!Array.isArray(value)) {
			return '';
		}

		const presets = this.getPresets();

		const queryStringChunks = value.map((rangedPresetValue) => {
			let fromValue = '';
			let toValue = '';

			if (rangedPresetValue.preset) {
				// if the query string is used by the client, let's display the preset only
				// to be more user friendly.
				if (target === QueryStringTarget.Client) {
					return rangedPresetValue.preset;
				} else {
					// if this is going to the server, attempt to get the full value
					const presetFn = presets[rangedPresetValue.preset];

					if (presetFn) {
						const preset = presetFn(filter?.options?.ignoreTimezone);
						fromValue = !isGenericEmptyValue(preset.fromValue) ? preset.fromValue : '';
						toValue = !isGenericEmptyValue(preset.toValue) ? preset.toValue : '';
					} else {
						console.warn('Invalid preset provided:', rangedPresetValue.preset);
						return '';
					}
				}
			} else {
				fromValue = !isGenericEmptyValue(rangedPresetValue.fromValue) ? rangedPresetValue.fromValue : '';
				toValue = !isGenericEmptyValue(rangedPresetValue.toValue) ? rangedPresetValue.toValue : '';
			}

			return `${fromValue}${RangedValueDelimiter}${toValue}`;
		});

		return queryStringChunks.join(MultipleValueDelimiter);
	};

	public isEmptyValue = (value: RangedPresetValue[]): boolean => {
		if (isGenericEmptyValue(value)) {
			return true;
		}

		if (Array.isArray(value) && (value.length === 0 || value.every((val) => isGenericEmptyValue(val)))) {
			return true;
		}

		return false;
	};
}

/**
 * Implementation of the a range preset provider specific to dates
 */
export class MultipleDateRangePresetProvider extends MultipleRangePresetProvider {
	public getPresets = () => MultipleDateRangePresets;
	public isValidRangePresetValue = (value: RangedPresetValue) => {
		const fromValue = value.fromValue ? moment(value.fromValue) : null;
		const toValue = value.toValue ? moment(value.toValue) : null;

		const fromValueValid = fromValue === null || fromValue.isValid();
		const toValueValid = toValue === null || toValue.isValid();

		if (!fromValueValid || !toValueValid) {
			return false;
		}

		return true;
	};
	public getComponent = () => MultipleDateRangePresetFilterEdit;
}
